import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import {
  ClassicEditor,
  Alignment,
  Autoformat,
  Bold,
  Code,
  Italic,
  Strikethrough,
  Subscript,
  Superscript,
  Underline,
  BlockQuote,
  CloudServices,
  CodeBlock,
  Essentials,
  FindAndReplace,
  Font,
  Heading,
  Highlight,
  HorizontalLine,
  GeneralHtmlSupport,
  AutoImage,
  Image,
  ImageCaption,
  ImageInsert,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  PictureEditing,
  Indent,
  IndentBlock,
  TextPartLanguage,
  AutoLink,
  Link,
  LinkImage,
  List,
  ListProperties,
  TodoList,
  MediaEmbed,
  Mention,
  PageBreak,
  Paragraph,
  PasteFromOffice,
  RemoveFormat,
  SpecialCharacters,
  SpecialCharactersEssentials,
  Style,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  WordCount,
  EditorConfig,
  Plugin,
  ButtonView,
} from 'ckeditor5';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { take } from 'rxjs';
import { formControl, FormControlExtended } from '@al00x/forms';
// import { formControl, FormControlExtended } from '@/core';

const UPLOAD_BTN_CLASS = 'ck-custom-upload-image-btn';
const UPLOAD_BTN_TOOLBAR = 'uploadImage';


@Component({
  selector: 'feature-editor',
  standalone: true,
  imports: [CKEditorModule, FormsModule, ReactiveFormsModule],
  templateUrl: './editor.component.html',
  styleUrl: './editor.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class EditorComponent implements OnInit, OnDestroy {
  @Input() control: FormControlExtended = formControl<string | undefined>();

  data = '';
  editor = ClassicEditor;
  config: EditorConfig = {
    extraPlugins: [UploadImagePlugin],
    language: 'ar',
    licenseKey: 'GPL',
    toolbar: {
      shouldNotGroupWhenFull: true,
      items: [
        'undo',
        'redo',
        '|',
        'heading',
        '|',
        'findAndReplace',
        'selectAll',
        '|',
        'link',
        'insertImage',
        'insertTable',
        'blockQuote',
        'mediaEmbed',
        'codeBlock',
        '|',
        'pageBreak',
        'horizontalLine',
        'specialCharacters',
        '|',
        'bold',
        'italic',
        'underline',
        'strikethrough',
        '|',
        'fontSize',
        'fontFamily',
        'fontColor',
        'fontBackgroundColor',
        'highlight',
        'code',
        '|',
        'removeFormat',
        '|',
        'alignment',
        'bulletedList',
        'numberedList',
        'todoList',
        '|',
        'outdent',
        'indent',
        UPLOAD_BTN_TOOLBAR,
      ],
    },
    fontSize: {
      options: [8, 10, 12, 14, 16, 18, 20, 24, 28, 32],
    },
    heading: {
      options: [
        {
          model: 'paragraph',
          title: 'Paragraph',
          class: 'ck-heading_paragraph',
        },
        {
          model: 'heading1',
          title: 'Heading 1',
          class: 'ck-heading_heading1',
          view: {
            name: 'h1',
            classes: ['text-[4rem]', 'leading-[6rem]'],
          },
        },
        {
          model: 'heading2',
          title: 'Heading 2',
          class: 'ck-heading_heading2',
          view: {
            name: 'h2',
            classes: ['text-5xl', 'leading-[4.5rem]'],
          },
        },
        {
          model: 'heading3',
          title: 'Heading 3',
          class: 'ck-heading_heading3',
          view: {
            name: 'h3',
            classes: ['text-[2rem]', 'leading-[3.74rem]'],
          },
        },
        {
          model: 'heading4',
          title: 'Heading 4',
          class: 'ck-heading_heading4',
          view: {
            name: 'h4',
            classes: ['text-2xl', 'leading-9'],
          },
        },
        {
          model: 'heading5',
          title: 'Heading 5',
          class: 'ck-heading_heading5',
          view: {
            name: 'h5',
            classes: ['text-xl', 'leading-9]'],
          },
        },
        {
          model: 'heading6',
          title: 'Heading 6',
          class: 'ck-heading_heading6',
          view: {
            name: 'h6',
            classes: ['text-base'],
          },
        },
      ],
    },
    plugins: [
      Alignment,
      Autoformat,
      AutoImage,
      AutoLink,
      BlockQuote,
      Bold,
      CloudServices,
      Code,
      CodeBlock,
      Essentials,
      FindAndReplace,
      Font,
      GeneralHtmlSupport,
      Heading,
      Highlight,
      HorizontalLine,
      Image,
      ImageCaption,
      ImageInsert,
      ImageResize,
      ImageStyle,
      ImageToolbar,
      Indent,
      IndentBlock,
      Italic,
      Link,
      LinkImage,
      List,
      ListProperties,
      MediaEmbed,
      Mention,
      PageBreak,
      Paragraph,
      PasteFromOffice,
      PictureEditing,
      RemoveFormat,
      SpecialCharacters,
      SpecialCharactersEssentials,
      Strikethrough,
      Style,
      Subscript,
      Superscript,
      Table,
      TableCaption,
      TableCellProperties,
      TableColumnResize,
      TableProperties,
      TableToolbar,
      TextPartLanguage,
      TextTransformation,
      TodoList,
      Underline,
      WordCount,
    ],
    fontColor: {
      colors: [
        {
          color: '#0064bc',
          label: 'Blue',
        },
        {
          color: '#ed6109',
          label: 'Orange',
        },
        {
          color: '#896245',
          label: 'Brown',
        },
        {
          color: '#896245',
          label: 'Brown (alt)',
        },
        {
          color: '#000000',
          label: 'Black',
        },
        {
          color: '#ffffff',
          label: 'White',
        },
      ],
    },

    image: {
      resizeOptions: [
        {
          name: 'resizeImage:original',
          label: 'Default image width',
          value: null,
        },
        {
          name: 'resizeImage:50',
          label: '50% page width',
          value: '50',
        },
        {
          name: 'resizeImage:75',
          label: '75% page width',
          value: '75',
        },
      ],
      toolbar: [
        'imageTextAlternative',
        'toggleImageCaption',
        '|',
        'imageStyle:inline',
        'imageStyle:wrapText',
        'imageStyle:breakText',
        '|',
        'resizeImage',
      ],
      insert: {
        integrations: ['url'],
      },
    },
  };

  ngOnInit(): void {
    this.data = this.control.value ?? '';
  }

  change(data: string) {
    this.control.setValue(data);
  }

  uploadImage() {
    dialog$
      .imageUpload()
      .afterSubmit()
      .pipe(take(1))
      .subscribe((result) => {
        const img = `<img src="${result.image.imageUrl}" />`;
        const val = this.control.value ?? '';
        this.data = val + img;
        this.control.setValue(this.data);
      });
  }

  onReady() {
    this.setUploadEventListener('add');
  }

  setUploadEventListener(type: 'add' | 'remove') {
    const uploadBtn = Array.from(document.getElementsByClassName(UPLOAD_BTN_CLASS))?.at(0);
    if (!uploadBtn) return;
    type === 'add' ? uploadBtn.addEventListener('click', () => {
      this.uploadImage();
    }) : uploadBtn.removeEventListener('click', () => {
      this.uploadImage();
    });
  }

  ngOnDestroy(): void {
    this.setUploadEventListener('remove');
  
  }
}



export default class UploadImagePlugin extends Plugin {
  init() {
    const editor = this.editor;

    editor.ui.componentFactory.add(UPLOAD_BTN_TOOLBAR, locale => {
      const view = new ButtonView(locale);

      view.set({
        label: 'Upload Image',
        withText: true,
        tooltip: true,
        class: UPLOAD_BTN_CLASS
      });

      return view;
    });
  }
}